import { StorageSerializers } from '@vueuse/core'

export function useMediaSettings(options: {
  enableCamera?: boolean
  enableMicrophone?: boolean
  prefixKey?: string
  immediate?: boolean
}) {
  const {
    enableCamera = false,
    enableMicrophone = false,
    prefixKey = 'default',
    immediate = true,
  } = options

  const currentCamera = useLocalStorage(`camera-${prefixKey}`, {}, { serializer: StorageSerializers.object })
  const currentMicrophone = useLocalStorage(`microphone-${prefixKey}`, {}, { serializer: StorageSerializers.object })
  const currentSpeaker = useLocalStorage(`speaker-${prefixKey}`, {}, { serializer: StorageSerializers.object })

  const currentCameraDeviceId = computed(() => currentCamera.value?.deviceId)
  const currentMicrophoneDeviceId = computed(() => currentMicrophone.value?.deviceId)

  const {
    videoInputs: cameras,
    audioInputs: microphones,
    audioOutputs: speakers,
  } = useDevicesList({
    requestPermissions: true,
    onUpdated() {
      if (!cameras.value.find(i => i.deviceId === currentCamera.value?.deviceId))
        currentCamera.value = cameras.value[0]

      if (!microphones.value.find(i => i.deviceId === currentMicrophone.value?.deviceId))
        currentMicrophone.value = microphones.value[0]

      if (!speakers.value.find(i => i.deviceId === currentSpeaker.value?.deviceId))
        currentSpeaker.value = speakers.value[0]
    },
  })

  const userMedia = useUserMedia({
    constraints: reactive({
      video: enableCamera ? { deviceId: currentCameraDeviceId } as MediaTrackConstraints : false,
      audio: enableMicrophone ? { deviceId: currentMicrophoneDeviceId } as MediaTrackConstraints : false,
    }),
    autoSwitch: true,
  }) as ReturnType<typeof useUserMedia>

  watch([currentCamera, currentMicrophone], () => {
    userMedia.restart()
  }, { flush: 'sync' })

  if (immediate) {
    if (enableCamera || enableMicrophone)
      userMedia.enabled.value = true
  }

  return {
    currentCamera,
    currentMicrophone,
    currentSpeaker,

    cameras,
    microphones,
    speakers,

    ...userMedia,
  }
}
